// Shared look for the admin Usage pages — same tokens as the admin
// Dashboard (src/pages/admin/Dashboard.js) so the two read as one product.
export const C = {
  bg: "#F1F1F1",
  surface: "#FFFFFF",
  ink: "#282828",
  primary: "#16498A",
  tint1: "#4B7FBE",
  tint2: "#8FB2DA",
  tintBg: "#EAF1FA",
  gray1: "#9AA0A6",
  gray3: "#C7CACD",
  gray6: "#E4E5E7",
  gray7: "#EDEDED",
  textSecondary: "#8a8a8a",
  textBody: "#4a4a4a",
  textFaint: "#a4a4a4",
  border: "rgba(40,40,40,0.06)",
  error: "#B42318",
  errorBg: "#FEF3F2",
  ok: "#067647",
  okBg: "#ECFDF3",
  warn: "#B54708",
  warnBg: "#FFFAEB",
};

export const FONT = "'Roboto Condensed', -apple-system, BlinkMacSystemFont, sans-serif";
export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

export const cardSx = { borderRadius: "24px", background: C.surface, border: `1px solid ${C.border}` };

export const ROLE_ORDER = ["patient", "therapist", "admin", "operator", "mixed", "guest"];
export const ROLE_COLORS = {
  patient: C.primary,
  therapist: C.tint1,
  admin: C.ink,
  operator: C.gray1,
  mixed: C.tint2,
  guest: C.gray3,
};
export const ROLE_LABELS = {
  patient: "Patient",
  therapist: "Therapist",
  admin: "Admin",
  operator: "Operator",
  mixed: "Multiple roles",
  guest: "Guest",
};

// Multi-role users whose portal couldn't be determined are logged as e.g.
// "therapist+patient" — grouped as "mixed" in charts, spelled out in pills.
export const isMixedRole = (role) => typeof role === "string" && role.includes("+");
export const roleKey = (role) => (isMixedRole(role) ? "mixed" : role);
export const roleLabel = (role) =>
  isMixedRole(role) ? role.split("+").map((r) => ROLE_LABELS[r] || r).join(" + ") : ROLE_LABELS[role] || role;
export const roleColor = (role) => ROLE_COLORS[roleKey(role)] || C.gray1;

// What each logged failure category means. expected = part of normal
// operation (shown muted, excluded from the error rate).
export const CATEGORY_INFO = {
  server_error: { label: "Server errors", hint: "The server crashed or failed (5xx)", expected: false },
  forbidden: { label: "Permission denied", hint: "User lacked access to the resource", expected: false },
  not_found: { label: "Not found", hint: "Requested item does not exist", expected: false },
  bad_request: { label: "Bad request", hint: "Invalid input sent by the app", expected: false },
  rate_limited: { label: "Rate limited", hint: "Too many attempts from one user/IP", expected: false },
  client_error: { label: "Other client errors", hint: "Other 4xx responses", expected: false },
  session_refresh: { label: "Session auto-refreshed", hint: "Short-lived token expired and was renewed silently", expected: true },
  session_expired: { label: "Session expired (logged out)", hint: "Long session ended — the user had to log in again", expected: true },
  login_failed: { label: "Wrong email/password", hint: "Login with incorrect credentials", expected: true },
  not_registered: { label: "Not registered yet", hint: "Customer tried to log in before creating an account", expected: true },
  account_inactive: { label: "Inactive account", hint: "Deactivated account tried to log in", expected: true },
  wrong_code: { label: "Wrong code", hint: "Login, sign-up or reset code mistyped or expired", expected: true },
  expired_link: { label: "Expired download link", hint: "Old report download link reopened", expected: true },
  no_data: { label: "No data yet", hint: "Lookup returned an empty result", expected: true },
  webhook_retry: { label: "Shopify retry", hint: "Order update arrived before the order — Shopify retries", expected: true },
};
export const categoryLabel = (c) => CATEGORY_INFO[c]?.label || c || "–";

export const METHOD_COLORS = {
  GET: { color: C.primary, bg: C.tintBg },
  POST: { color: C.ok, bg: C.okBg },
  PUT: { color: C.warn, bg: C.warnBg },
  PATCH: { color: C.warn, bg: C.warnBg },
  DELETE: { color: C.error, bg: C.errorBg },
};

export function statusStyle(code) {
  if (code >= 500) return { color: C.error, bg: C.errorBg };
  if (code >= 400) return { color: C.warn, bg: C.warnBg };
  return { color: C.ok, bg: C.okBg };
}

export const formatNumber = (n) => (n === null || n === undefined ? "–" : Number(n).toLocaleString("en-US"));

export const formatMs = (ms) => {
  if (ms === null || ms === undefined) return "–";
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
};

export const formatDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "–";

export const formatTimeAgo = (iso) => {
  if (!iso) return "–";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};
